// src\pages\ViewPhotos.js

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import "../styles/ViewPhotos.css";
import { isTokenValid } from "../utils/isTokenValid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import timeAgo from "../utils/timeAgo";

const ViewPhotos = ({ userRole, schoolName }) => {
  const [feedPhotos, setFeedPhotos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMorePhotos, setHasMorePhotos] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hashtags, setHashtags] = useState([]);
  const [selectedHashtag, setSelectedHashtag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const observer = useRef(null);

  const fetchPhotos = async (page, hashtag = null, allSchools = showAllSchools) => {
    if (!isTokenValid()) {
      alert("Your session has expired. Please log in again.");
      localStorage.removeItem("token");
      window.location.href = "/login";
      return;
    }

    const token = localStorage.getItem("token");
    setIsLoading(true);

    try {
      const url = hashtag
        ? "https://ghrianghraffy.com/photos/hashtag-photos"
        : "https://ghrianghraffy.com/photos/view-photos";

      const params = hashtag
        ? { offset: page * 20, limit: 20, hashtag, all_schools: allSchools }
        : { offset: page * 20, limit: 20, all_schools: allSchools };

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const newPhotos = response.data;
      setFeedPhotos((prevPhotos) =>
        page === 0 ? newPhotos : [...prevPhotos, ...newPhotos]
      );

      // Updated logic: hasMorePhotos only true if 20 photos returned
      setHasMorePhotos(newPhotos.length === 20);
    } catch (err) {
      console.error("Error fetching photos:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchHashtags = async () => {
      if (!searchQuery) {
        setHashtags([]);
        return;
      }

      if (!isTokenValid()) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
        return;
      }

      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          `https://ghrianghraffy.com/photos/hashtags?query=${encodeURIComponent(
            searchQuery
          )}&all_schools=${showAllSchools}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setHashtags(response.data);
      } catch (err) {
        console.error("Error fetching hashtags:", err);
      }
    };

    const timeoutId = setTimeout(fetchHashtags, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, showAllSchools]);

  const handleHashtagClick = (hashtag) => {
    setSelectedHashtag(hashtag);
    setFeedPhotos([]);
    setCurrentPage(0);
    fetchPhotos(0, hashtag);
  };

  const handleClearSearch = () => {
    setSelectedHashtag(null);
    setCurrentPage(0);
    fetchPhotos(0);
  };

  const handleToggleSchools = () => {
    setShowAllSchools((prev) => !prev);
    setFeedPhotos([]);
    setCurrentPage(0);
    setSelectedHashtag(null);
    fetchPhotos(0, null, !showAllSchools);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const initialFeedLoadedRef = useRef(false);
  useEffect(() => {
    if (!["PARENT", "STAFF", "PRINCIPAL"].includes(userRole)) return;
    if (initialFeedLoadedRef.current) return;
    initialFeedLoadedRef.current = true;
    fetchPhotos(0);
  }, [userRole]);

  const setLastPhotoRef = (node) => {
    if (observer.current) observer.current.disconnect();

    if (node && hasMorePhotos && !isLoading) {
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setCurrentPage((prev) => prev + 1);
          }
        },
        { threshold: 0.5 }
      );
      observer.current.observe(node);
    }
  };

  useEffect(() => {
    if (currentPage > 0) {
      fetchPhotos(currentPage, selectedHashtag);
    }
  }, [currentPage]);

  return (
    <div>
      <h1>
        {schoolName
          ? `${schoolName}'s Photos`
          : userRole === "PARENT"
            ? "Your School's Photos"
            : "Our School's Photos"}
      </h1>

      <div className="toggle-container">
        <label>
          <input
            type="checkbox"
            checked={showAllSchools}
            onChange={handleToggleSchools}
          />
          Show All Schools
        </label>
      </div>

      <input
        type="text"
        placeholder="Search by hashtag..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="search-bar"
      />

      {hashtags.length > 0 && (
        <div className="hashtag-suggestions">
          {hashtags.map((hashtagObj) => (
            <span
              key={hashtagObj.hashtag}
              className="hashtag"
              onClick={() => handleHashtagClick(hashtagObj.hashtag)}
            >
              {hashtagObj.hashtag}
            </span>
          ))}
        </div>
      )}

      {selectedHashtag && (
        <div className="search-results-header">
          <h2>Results for {selectedHashtag}</h2>
          <button onClick={handleClearSearch} className="clear-search-button">
            Clear Search
          </button>
        </div>
      )}

      <div className="feed">
        {feedPhotos.length > 0 ? (
          feedPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="feed-photo"
              ref={index === feedPhotos.length - 1 ? setLastPhotoRef : null}
              onClick={() => setLightboxIndex(index)}
            >
              <img
                src={`https://ghrianghraffy.com/${photo.thumbnail_path}`}
                alt={photo.story || "Photo"}
                className="feed-photo-thumbnail"
                loading="lazy"
              />
              <p>{photo.story}</p>
              <p>
                Uploaded by{" "}
                {photo.uploaded_by?.replace("%20", " ") || "Unknown"},{" "}
                {photo.created_at ? timeAgo(photo.created_at) : "N/A"}
              </p>
            </div>
          ))
        ) : (
          <p>{isLoading ? "Loading photos..." : "No photos to display."}</p>
        )}
      </div>

      {lightboxIndex !== null && feedPhotos[lightboxIndex] && (
        <Lightbox
          mainSrc={`https://ghrianghraffy.com/${feedPhotos[lightboxIndex].thumbnail_path}`}
          nextSrc={
            feedPhotos[(lightboxIndex + 1) % feedPhotos.length]?.thumbnail_path &&
            `https://ghrianghraffy.com/${feedPhotos[(lightboxIndex + 1) % feedPhotos.length].thumbnail_path}`
          }
          prevSrc={
            feedPhotos[(lightboxIndex + feedPhotos.length - 1) % feedPhotos.length]?.thumbnail_path &&
            `https://ghrianghraffy.com/${feedPhotos[(lightboxIndex + feedPhotos.length - 1) % feedPhotos.length].thumbnail_path}`
          }
          onCloseRequest={() => setLightboxIndex(null)}
          onMovePrevRequest={() =>
            setLightboxIndex(
              (lightboxIndex + feedPhotos.length - 1) % feedPhotos.length
            )
          }
          onMoveNextRequest={() =>
            setLightboxIndex((lightboxIndex + 1) % feedPhotos.length)
          }
          imageCaption={
            feedPhotos[lightboxIndex].story +
            " — Uploaded by " +
            (feedPhotos[lightboxIndex].uploaded_by?.replace("%20", " ") ||
              "Unknown")
          }
        />
      )}

      {isLoading && <p className="loading-indicator">Loading more photos...</p>}
      {showBackToTop && (
        <button
          className="back-to-search-button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to search"
          title="Back to search"
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </button>
      )}
    </div>
  );
};

export default ViewPhotos;